import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    concatMap,
    exhaustMap,
    filter,
    map,
    merge,
    of,
    withLatestFrom,
} from "rxjs";
import { SocketTransportService } from "../../../core/realtime/socket-transport.service";
import { SessionActions } from "../../authentication/store/session.actions";
import { sessionFeature } from "../../authentication/store/session.reducer";
import { NotificationApiService } from "../data-access/notification-api.service";
import {
    DashboardNotification,
    NotificationErrorCode,
} from "../models/notification.models";
import { NotificationActions } from "./notification.actions";
import { notificationFeature } from "./notification.reducer";

interface SharedImageMessage {
    typeId: string;
    record: { sharer: string; imageKeys: string[]; bundleId?: string };
}

interface AddedCommentMessage {
    bundleId: string;
    comment: { username: string; timestamp: number };
}

interface UnshareSelfMessage {
    performer: string;
    bundleId: string;
}

@Injectable()
export class NotificationEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(NotificationApiService);
    private readonly socket = inject(SocketTransportService);

    readonly initialLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.currentUserLoaded),
            map(() => NotificationActions.loadRequested({ latestId: null }))
        )
    );

    readonly loadOnOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.drawerOpened),
            withLatestFrom(
                this.store.select(notificationFeature.selectLoaded),
                this.store.select(notificationFeature.selectLoading)
            ),
            filter(([, loaded, loading]) => !loaded && !loading),
            map(() => NotificationActions.loadRequested({ latestId: null }))
        )
    );

    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadRequested),
            exhaustMap(({ latestId }) =>
                this.api.getAll(latestId).pipe(
                    map(({ currentPage, latestId: nextId }) =>
                        NotificationActions.loadSucceeded({
                            notifications: currentPage ?? [],
                            latestId: nextId ?? null,
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            NotificationActions.loadFailed({
                                error: this.errorCode(error),
                                operation: latestId
                                    ? "loadMore"
                                    : "initialLoad",
                            })
                        )
                    )
                )
            )
        )
    );

    readonly persistSeen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                NotificationActions.markSeenRequested,
                NotificationActions.markUnreadRequested
            ),
            concatMap((action) => {
                const seen =
                    action.type === NotificationActions.markSeenRequested.type;
                return this.api.setSeen(action.typeId, seen).pipe(
                    map(() =>
                        seen
                            ? NotificationActions.markSeenSucceeded()
                            : NotificationActions.markUnreadSucceeded()
                    ),
                    catchError((error: unknown) =>
                        of(
                            seen
                                ? NotificationActions.markSeenFailed({
                                      typeId: action.typeId,
                                      error: this.errorCode(error),
                                  })
                                : NotificationActions.markUnreadFailed({
                                      typeId: action.typeId,
                                      error: this.errorCode(error),
                                  })
                        )
                    )
                );
            })
        )
    );

    readonly clear$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.logoutCompleted),
            map(() => NotificationActions.cleared())
        )
    );

    readonly socketNotifications$ = createEffect(() =>
        merge(
            this.socket.on<SharedImageMessage>("shared_image").pipe(
                map(
                    (message): DashboardNotification => ({
                        gmvid: message.typeId,
                        type: "sharedImage",
                        sender: message.record.sharer,
                        receiver: "",
                        seen: false,
                        date: Date.now(),
                        typeId: message.typeId,
                        imageUrl: message.record.imageKeys,
                        bundleId: message.record.bundleId,
                    })
                )
            ),
            this.socket.on<AddedCommentMessage>("added_comment").pipe(
                map(
                    (message): DashboardNotification => ({
                        gmvid: "",
                        type: "addedComment",
                        sender: message.comment.username,
                        receiver: "",
                        seen: false,
                        date: message.comment.timestamp,
                        typeId: `comment:${message.bundleId}:${message.comment.timestamp}`,
                        bundleId: message.bundleId,
                    })
                )
            ),
            this.socket.on<UnshareSelfMessage>("unshare_self_bundle").pipe(
                map(
                    (message): DashboardNotification => ({
                        gmvid: "",
                        type: "unshareSelf",
                        sender: message.performer,
                        receiver: "",
                        seen: false,
                        date: Date.now(),
                        typeId: message.bundleId,
                        bundleId: message.bundleId,
                    })
                )
            )
        ).pipe(
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            map(([notification, user]) =>
                NotificationActions.notificationReceived({
                    notification: {
                        ...notification,
                        receiver: user?.username ?? "",
                    },
                })
            )
        )
    );

    private errorCode(error: unknown): NotificationErrorCode {
        if (!(error instanceof HttpErrorResponse)) return "unknown";
        if (error.status === 0) return "network";
        if (error.status === 401 || error.status === 403) return "unauthorized";
        if (error.status === 429) return "rateLimited";
        if (error.status >= 500) return "server";
        return "unknown";
    }
}
